import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaBook, FaChevronRight, FaGraduationCap } from 'react-icons/fa';
import DashboardLayout from '../../components/layout/DashboardLayout';
import API_BASE_URL from '../../config/api';

const COLOR_SCHEMES = [
  { bg: 'bg-blue-50', text: 'text-blue-600', accent: 'bg-blue-600', gradient: 'from-blue-500 to-indigo-600', light: 'bg-blue-100/50' },
  { bg: 'bg-emerald-50', text: 'text-emerald-600', accent: 'bg-emerald-600', gradient: 'from-emerald-500 to-teal-600', light: 'bg-emerald-100/50' },
  { bg: 'bg-orange-50', text: 'text-orange-600', accent: 'bg-orange-600', gradient: 'from-orange-500 to-red-600', light: 'bg-orange-100/50' },
  { bg: 'bg-purple-50', text: 'text-purple-600', accent: 'bg-purple-600', gradient: 'from-purple-500 to-violet-600', light: 'bg-purple-100/50' },
  { bg: 'bg-rose-50', text: 'text-rose-600', accent: 'bg-rose-600', gradient: 'from-rose-500 to-pink-600', light: 'bg-rose-100/50' },
  { bg: 'bg-amber-50', text: 'text-amber-600', accent: 'bg-amber-600', gradient: 'from-amber-500 to-yellow-600', light: 'bg-amber-100/50' },
];

const MyCourses = () => {
  const navigate = useNavigate();
  const [enrollments, setEnrollments] = useState([]);
  const [loading, setLoading] = useState(true);
  const user = JSON.parse(localStorage.getItem('user'));

  useEffect(() => {
    if (!user || !user.id) { navigate('/login'); return; }
    const fetchEnrollments = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/get_enrolled_months.php?user_id=${user.id}`);
        const result = await response.json();
        if (result.status === 'success') setEnrollments(result.data);
      } catch (error) {
        console.error("Error fetching enrollments:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchEnrollments();
  }, [user?.id, navigate]);

  if (!user || !user.id) return null;

  return (
    <DashboardLayout title="My Enrolled Months">
      <div className="space-y-4 sm:space-y-6 relative p-1">
        {/* Background Grid Pattern - More Distinct */}
        <div className="absolute inset-0 -z-10 bg-slate-50 bg-[linear-gradient(to_right,#cbd5e1_1.5px,transparent_1.5px),linear-gradient(to_bottom,#cbd5e1_1.5px,transparent_1.5px)] bg-[size:40px_40px] [mask-image:radial-gradient(ellipse_80%_60%_at_50%_0%,#000_80%,transparent_100%)] opacity-25"></div>

        <div className="bg-gradient-to-r from-slate-800 to-slate-900 rounded-xl sm:rounded-2xl p-6 sm:p-8 text-white shadow-xl relative overflow-hidden group/header">
          <div className="absolute top-0 right-0 w-64 h-64 bg-orange-500/10 rounded-full -mr-32 -mt-32 blur-3xl group-hover/header:bg-orange-500/20 transition-colors duration-700"></div>
          <div className="absolute bottom-0 left-0 w-32 h-32 bg-blue-500/10 rounded-full -ml-16 -mb-16 blur-2xl"></div>
          <div className="relative z-10 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div className="space-y-1">
              <h2 className="text-xl sm:text-2xl font-black tracking-tight">My Learning Portal</h2>
              <p className="text-slate-400 text-xs sm:text-sm font-medium max-w-md">Access your organized course materials and track your progress.</p>
            </div>
            {!loading && enrollments.length > 0 && (
              <div className="flex items-center gap-3 bg-white/5 backdrop-blur-md p-2 pr-4 rounded-2xl border border-white/10 self-start">
                <div className="w-10 h-10 bg-orange-500 rounded-xl flex items-center justify-center text-white font-black shadow-lg shadow-orange-500/20">
                  {enrollments.length}
                </div>
                <div>
                  <div className="text-[10px] font-black uppercase text-slate-400 leading-none">Active</div>
                  <div className="text-xs font-bold text-white uppercase tracking-widest">Sessions</div>
                </div>
              </div>
            )}
          </div>
        </div>

        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            {[1, 2, 3].map((i) => (
              <div key={i} className="bg-white/50 rounded-2xl border border-slate-100 p-6 space-y-5 animate-pulse">
                <div className="flex justify-between items-start">
                  <div className="w-12 h-12 bg-slate-200 rounded-xl"></div>
                  <div className="w-16 h-6 bg-slate-200 rounded-full"></div>
                </div>
                <div className="space-y-3">
                  <div className="h-4 bg-slate-200 rounded w-3/4"></div>
                  <div className="h-3 bg-slate-200 rounded w-1/2"></div>
                </div>
                <div className="pt-5 border-t border-slate-100 flex justify-between items-center">
                  <div className="h-3 bg-slate-200 rounded w-1/3"></div>
                  <div className="w-8 h-8 bg-slate-200 rounded-lg"></div>
                </div>
              </div>
            ))}
          </div>
        ) : enrollments.length === 0 ? (
          <div className="text-center py-20 bg-white/60 backdrop-blur-md rounded-2xl border border-slate-100 space-y-6 shadow-sm">
            <div className="w-20 h-20 bg-slate-50 rounded-full flex items-center justify-center mx-auto text-slate-300 text-4xl shadow-inner">
              <FaBook />
            </div>
            <div className="space-y-2">
              <h3 className="text-lg font-bold text-slate-800">No Enrolled Sessions Found</h3>
              <p className="text-slate-400 text-sm max-w-xs mx-auto font-medium">It looks like you haven't joined any classes yet. Start your journey today!</p>
            </div>
            <button
              onClick={() => navigate('/student/all-classes')}
              className="px-10 py-3.5 bg-orange-500 text-white font-black rounded-xl shadow-xl shadow-orange-500/30 text-xs uppercase tracking-widest hover:bg-orange-600 hover:-translate-y-1 transition-all active:scale-95"
            >
              Explore All Classes
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            {enrollments.map((en, index) => {
              const scheme = COLOR_SCHEMES[index % COLOR_SCHEMES.length];
              const glowColor = scheme.accent.split('-')[1]; // Extracts 'blue' from 'bg-blue-600'
              return (
                <div
                  key={en.id}
                  onClick={() => navigate(`/student/materials/${en.month_id}`)}
                  className={`${scheme.bg} rounded-2xl border border-slate-200/50 shadow-sm p-5 sm:p-6 flex flex-col hover:shadow-2xl hover:shadow-${glowColor}-500/20 hover:-translate-y-2 transition-all duration-500 cursor-pointer group relative overflow-hidden`}
                >
                  {/* Glassy overlay on hover */}
                  <div className="absolute inset-0 bg-white/0 group-hover:bg-white/30 transition-colors duration-500"></div>
                  
                  {/* Decorative Background Element */}
                  <div className={`absolute top-0 right-0 w-28 h-28 bg-gradient-to-br ${scheme.gradient} opacity-[0.1] rounded-bl-full -mr-10 -mt-10 group-hover:scale-125 transition-transform duration-700`}></div>
                  
                  <div className="flex justify-between items-start mb-6 relative z-10">
                    <div className={`w-14 h-14 bg-white ${scheme.text} rounded-2xl shadow-md flex items-center justify-center font-black text-base group-hover:scale-110 group-hover:rotate-3 transition-all duration-500 uppercase`}>
                      {en.month_name?.substring(0, 3)}
                    </div>
                    <span className={`flex items-center gap-1.5 px-3 py-1.5 bg-white/80 ${scheme.text} rounded-full text-[10px] font-extrabold uppercase tracking-widest border border-white shadow-sm`}>
                      <span className={`w-2 h-2 rounded-full ${scheme.accent} animate-pulse`}></span>
                      Current
                    </span>
                  </div>

                  <div className="space-y-1.5 mb-8 relative z-10">
                    <h3 className="text-base sm:text-lg font-extrabold text-slate-800 line-clamp-1 group-hover:text-slate-900 transition-colors tracking-tight leading-tight">{en.course_title}</h3>
                    <p className="text-[11px] text-slate-500/80 font-bold uppercase tracking-tight flex items-center gap-2">
                      <span className={`w-1 h-4 ${scheme.accent} rounded-full`}></span>
                      {en.month_name} Academic Session
                    </p>
                  </div>

                  <div className="mt-auto pt-5 border-t border-slate-900/5 flex items-center justify-between relative z-10">
                    <span className={`text-[11px] font-black ${scheme.text} uppercase tracking-widest flex items-center gap-2 group-hover:gap-4 transition-all duration-300`}>
                      <FaGraduationCap className="text-base" /> Access Materials
                    </span>
                    <div className={`p-2.5 bg-white ${scheme.text} rounded-xl group-hover:${scheme.accent} group-hover:text-white transition-all duration-500 shadow-md group-hover:rotate-12`}>
                      <FaChevronRight size={12} />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default MyCourses;
